import { observer } from 'mobx-react-lite';
import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Table,
  Segment,
  Header,
  Message,
  Icon,
  Dropdown,
  Button,
  MessageHeader,
} from 'semantic-ui-react';
import {
  cutlistCompleteInitialState,
  cutlistCompleteTypes,
} from '../../../app/stores/CRMStore/types';
import { useStore } from '../../../app/stores/store';
import './AllCompletedReport.css';

interface MetadataItem {
  elementName: string;
  elementValue: string;
  isDeleted: boolean;
}

interface ReelItem {
  id: string;
  title: string;
  current_task: string;
  current_subtask: string;
  status: string;
  type: string;
  element: string;
  assetCondition?: string;
  metadata?: MetadataItem[];
  [key: string]: any;
}

interface KeyValueEntry {
  key: string;
  value: string;
}

interface TransformedItem {
  data: KeyValueEntry[];
}

const replaceString: Record<string, string> = {
  assetCondition: 'Titlestatus',
  physical_location_shelf: 'Previous_Owner_Employee_ID',
  physical_location_vault: 'Previous_Owner_Employee_Name',
  asset_In_Vault: 'operator_complete_date_time',
  statusUpdateTime: 'qC_complete_date_time',
};

const transformToKeyValueArray = (data: ReelItem[]): TransformedItem[] => {
  return data.map((item: ReelItem) => {
    const baseFields: KeyValueEntry[] = Object.entries(item)
      .filter(([key]) => key !== 'metadata')
      .map(([key, value]) => ({
        key:
          replaceString[key]?.replaceAll(/ /g, '_') ||
          key?.replaceAll(/ /g, '_'),
        value:
          key === 'asset_In_Vault'
            ? String(value).replace(
                /(\d{2}-\d{2}-)(\d{5})/,
                (_, p1, year) => `${p1}${year.slice(-4)}`,
              )
            : String(value),
      }));

    const metadataFields: KeyValueEntry[] = (item.metadata || [])
      .filter((meta) => !meta.isDeleted)
      .map((meta) => ({
        key: meta.elementName?.replaceAll(/ /g, '_'),
        value: meta.elementValue,
      }));

    return { data: [...baseFields, ...metadataFields] };
  });
};

const AllCompletedReport = () => {
  const {
    NFAI_Restoration_SmartflowsStore: {
      getReel,
      employeeIdFilter,
      dropdownDetails,
      cutlistCompleteList,
      getCutlistCompleteList,
      cutlistCompleteFilmTitleList,
      getFilmTitle,
      cutlistCompleteLoader,
      cutlistCompleteReelList,
    },
  } = useStore();

  const [selectTitle, setSelectTitle] = useState('');
  const [selectReel, setSelectReel] = useState('');
  const [selectEmpId, setSelectEmpId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [prevOwnerEmpId, setPrevOwnerEmpId] = useState('');

  const itemsPerPage = 20;

  useEffect(() => {
    getFilmTitle();
    employeeIdFilter();
    // eslint-disable-next-line
  }, []);

  const completedItems = useMemo(
    () => transformToKeyValueArray(cutlistCompleteList),
    [cutlistCompleteList],
  );

  const sortedMetaKeys = useMemo(() => {
    const keySet = new Set<string>();
    completedItems.forEach((item) => {
      item.data.forEach((entry) => {
        keySet.add(entry.key);
      });
    });
    return Array.from(keySet).sort();
  }, [completedItems]);

  const orderedKeys = useMemo(() => {
    const fromStorage = localStorage.getItem('SmartTracker');
    if (fromStorage) {
      const baseKeys = fromStorage
        .split(',')
        .filter(
          (x) => x !== 'status' && x !== 'LockedBy' && x !== 'Submission_Date',
        );
      return [
        ...baseKeys,
        'operator_complete_date_time',
        'qC_complete_date_time',
      ];
    }
    return sortedMetaKeys;
  }, [sortedMetaKeys]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return completedItems.slice(startIndex, startIndex + itemsPerPage);
  }, [completedItems, currentPage]);

  const csvData = useMemo(() => {
    return completedItems.map((item) => {
      const row: Record<string, string> = {};
      item.data.forEach((entry) => {
        const transformedKey = entry.key
          .replaceAll('_', ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        row[transformedKey] = entry.value;
      });

      const orderedRow: Record<string, string> = {};
      orderedKeys.forEach((key) => {
        const transformedKey = key
          .replaceAll('_', ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        orderedRow[transformedKey] = row[transformedKey] || '';
      });

      return orderedRow;
    });
  }, [completedItems, orderedKeys]);

  const totalPages = Math.ceil(completedItems.length / itemsPerPage);
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => handlePageChange(currentPage - 1);
  const handleNextPage = () => handlePageChange(currentPage + 1);

  return (
    <Segment padded className="all-completed-report-container">
      <Header as="h3" icon textAlign="center">
        <Icon name="file alternate outline" />
        Completed Reports
        <Header.Subheader>
          Detailed list of all completed cutlists
        </Header.Subheader>
      </Header>

      <div
        className="report-controls"
        style={{ justifyContent: 'space-between' }}
      >
        <div className="report-controls">
          <Dropdown
            className="dropdown-film"
            placeholder="Select Title"
            clearable
            fluid
            search
            selection
            disabled={cutlistCompleteLoader.tableLoader}
            loading={cutlistCompleteLoader.filmTitleLoader}
            options={cutlistCompleteFilmTitleList}
            value={selectTitle}
            onChange={(_, { value }) => {
              setCurrentPage(1);
              const parsedValue = value?.toString();
              setSelectTitle(parsedValue || '');
              setSelectReel('');
              if (value?.toString() !== '') {
                const payload: cutlistCompleteTypes = {
                  ...cutlistCompleteInitialState,
                  title: parsedValue || '',
                  reelName: '',
                  empId: selectEmpId,
                  previousOwnerEmployeeID: prevOwnerEmpId,
                };
                getCutlistCompleteList(payload);
                value && getReel?.(value as string);
              }
            }}
          />
          {selectTitle && (
            <Dropdown
              className="dropdown-reel"
              placeholder="Select Reel"
              clearable
              fluid
              search
              selection
              disabled={cutlistCompleteLoader.tableLoader}
              options={cutlistCompleteReelList}
              value={selectReel}
              onChange={(_, { value }) => {
                setCurrentPage(1);
                const parsedValue = value?.toString();
                setSelectReel(parsedValue || '');
                const payload: cutlistCompleteTypes = {
                  ...cutlistCompleteInitialState,
                  title: selectTitle,
                  reelName: parsedValue || '',
                  empId: selectEmpId,
                  previousOwnerEmployeeID: prevOwnerEmpId,
                };
                getCutlistCompleteList(payload);
              }}
            />
          )}
          <Dropdown
            className="dropdown-empid"
            placeholder="Select Emp ID"
            clearable
            fluid
            search
            selection
            disabled={cutlistCompleteLoader.tableLoader}
            options={dropdownDetails.empIdList}
            value={selectEmpId}
            onChange={(_, { value }) => {
              setCurrentPage(1);
              setSelectEmpId(value?.toString() || '');
              const payload: cutlistCompleteTypes = {
                ...cutlistCompleteInitialState,
                title: selectTitle,
                reelName: selectReel,
                empId: value?.toString() || '',
                previousOwnerEmployeeID: prevOwnerEmpId,
              };
              getCutlistCompleteList(payload);
            }}
          />
          <Dropdown
            className="dropdown-empid"
            placeholder="Filter by Previous Owner Emp ID"
            clearable
            fluid
            search
            selection
            disabled={cutlistCompleteLoader.tableLoader}
            options={[
              'R1498', // Sabir
              'R1187', // Muthumaniganesh
              'R4297', // N.Arun
              'R1509', // DilipVarma.H
              'R4483', // RANJITKUMAR.R
              'R4504', // PrashantaKumarBehera ( HYD )
              'R1956', // VeeramallaAnjiAnjaiah ( HYD )
              'R4509', // AdityaRaj ( HYD )
            ].map((val) => ({ key: val, text: val, value: val }))}
            value={prevOwnerEmpId}
            onChange={(_, { value }) => {
              setCurrentPage(1);
              setPrevOwnerEmpId(value as string);
              const payload: cutlistCompleteTypes = {
                ...cutlistCompleteInitialState,
                title: selectTitle,
                reelName: selectReel,
                empId: selectEmpId,
                previousOwnerEmployeeID: value?.toString() || '',
              };
              getCutlistCompleteList(payload);
            }}
          />
        </div>
        {selectTitle === '' ||
        cutlistCompleteLoader.tableLoader ||
        completedItems.length > 0 ? null : (
          <CSVLink data={csvData} filename="CulistAllCompleteReport.csv">
            <Button color="green" icon labelPosition="left">
              <Icon name="download" />
              Export CSV
            </Button>
          </CSVLink>
        )}
      </div>

      {cutlistCompleteLoader.tableLoader ? (
        <div className="AllCompletedReport-loader" />
      ) : selectTitle === '' ? (
        <Message info style={{ marginBottom: '10px' }}>
          <MessageHeader>{"Please Select 'Film Title'"}</MessageHeader>
        </Message>
      ) : paginatedItems.length ? (
        <>
          <Table
            celled
            striped
            color="blue"
            size="small"
            className="all-completed-report-table"
          >
            <Table.Header>
              <Table.Row>
                {orderedKeys.map((key) => (
                  <Table.HeaderCell key={key}>
                    {key
                      .replaceAll('_', ' ')
                      .split(' ')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(' ')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedItems.map((item, i) => (
                <Table.Row key={i}>
                  {orderedKeys.map((key) => {
                    const entry = item.data.find((entry) => entry.key === key);
                    return (
                      <Table.Cell key={key}>{entry?.value || ''}</Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <div
              className="AllCompletedReport-pagination-wrapper"
              style={{ textAlign: 'center', marginTop: '1rem' }}
            >
              <div className="AllCompletedReport-pagination">
                <div
                  className="AllCompletedReport-page-numbers prev"
                  onClick={handlePrevPage}
                >
                  prev
                </div>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (pageNum) =>
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      Math.abs(pageNum - currentPage) <= 2,
                  )
                  .map((pageNum, idx, arr) => {
                    const prev = arr[idx - 1];
                    return (
                      <React.Fragment key={pageNum}>
                        {idx > 0 && pageNum - prev > 1 && (
                          <span className="AllCompletedReport-page-numbers dots">
                            ...
                          </span>
                        )}
                        <span
                          className={`AllCompletedReport-page-numbers ${currentPage === pageNum ? 'current' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </span>
                      </React.Fragment>
                    );
                  })}
                <div
                  className="AllCompletedReport-page-numbers next"
                  onClick={handleNextPage}
                >
                  next
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Message icon warning className="all-completed-report-empty">
          <Icon name="inbox" />
          <Message.Content>
            <Message.Header>No completed reports</Message.Header>
            You currently have no cutlist marked as completed.
          </Message.Content>
        </Message>
      )}
    </Segment>
  );
};

export default observer(AllCompletedReport);
